import { useCallback, useEffect, useRef, useState } from "react";

interface UsePollingOptions<T> {
  /** Función async que carga los datos. Debe ser estable (memoized o ref). */
  fetcher: () => Promise<T>;
  /** Intervalo en ms entre fetches automáticos. Default 30 segundos. */
  intervalMs?: number;
  /** Si la pestaña está oculta, pausar polling. Default true. */
  pauseWhenHidden?: boolean;
  /** Comparador para detectar si hubo cambios entre cargas. */
  detectChange?: (prev: T | null, next: T) => boolean;
}

interface UsePollingResult<T> {
  data: T | null;
  loading: boolean;          // primer fetch
  refreshing: boolean;       // fetch en background
  error: string | null;
  lastUpdatedAt: Date | null;
  hasNewSinceLast: boolean;
  refresh: () => Promise<void>;
}

/**
 * Hook reusable para fetcheo periódico con detección de cambios.
 * No depende de librerías externas — vanilla React.
 *
 * Uso típico:
 *   const { data, loading, refreshing, refresh, lastUpdatedAt, hasNewSinceLast }
 *     = usePolling({
 *         fetcher: () => api.list(),
 *         intervalMs: 30_000,
 *         detectChange: (prev, next) => prev?.length !== next?.length,
 *       });
 */
export function usePolling<T>({
  fetcher,
  intervalMs = 30_000,
  pauseWhenHidden = true,
  detectChange,
}: UsePollingOptions<T>): UsePollingResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [hasNewSinceLast, setHasNewSinceLast] = useState(false);

  // Refs para evitar stale closures dentro del setInterval
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const detectChangeRef = useRef(detectChange);
  detectChangeRef.current = detectChange;
  const dataRef = useRef<T | null>(null);
  dataRef.current = data;
  const inFlightRef = useRef(false);

  const doFetch = useCallback(async (isManual: boolean) => {
    if (inFlightRef.current) return;  // dedupe simultáneos
    inFlightRef.current = true;

    if (isManual || dataRef.current === null) {
      setRefreshing(true);
    } else {
      setRefreshing(true);
    }

    try {
      const next = await fetcherRef.current();
      const prev = dataRef.current;
      const changed = detectChangeRef.current
        ? detectChangeRef.current(prev, next)
        : prev !== null && JSON.stringify(prev) !== JSON.stringify(next);

      setData(next);
      setLastUpdatedAt(new Date());
      setError(null);

      // hasNewSinceLast persiste hasta que el usuario haga refresh manual
      // o el siguiente fetch automático no traiga cambios
      if (changed && prev !== null) {
        setHasNewSinceLast(true);
      } else if (isManual) {
        setHasNewSinceLast(false);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error desconocido";
      setError(msg);
      // eslint-disable-next-line no-console
      console.error("[usePolling] Fetch falló:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      inFlightRef.current = false;
    }
  }, []);

  // Fetch inicial al montar
  useEffect(() => {
    doFetch(false);
  }, [doFetch]);

  // Polling con setInterval (con respeto a visibility)
  useEffect(() => {
    if (intervalMs <= 0) return;

    const tick = () => {
      if (pauseWhenHidden && document.hidden) return;
      doFetch(false);
    };

    const id = setInterval(tick, intervalMs);
    return () => clearInterval(id);
  }, [doFetch, intervalMs, pauseWhenHidden]);

  // Cuando la pestaña vuelve a estar visible, hacer fetch inmediato
  // (suele ser que el usuario regresó después de minutos)
  useEffect(() => {
    if (!pauseWhenHidden) return;

    const onVisibilityChange = () => {
      if (!document.hidden && lastUpdatedAt) {
        const stale = Date.now() - lastUpdatedAt.getTime() > intervalMs;
        if (stale) doFetch(false);
      }
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [doFetch, intervalMs, lastUpdatedAt, pauseWhenHidden]);

  const refresh = useCallback(() => doFetch(true), [doFetch]);

  return {
    data,
    loading,
    refreshing,
    error,
    lastUpdatedAt,
    hasNewSinceLast,
    refresh,
  };
}
